/**
 * product.js - Galerie, selecteur de variantes et ajout au panier.
 */

(() => {
  'use strict';

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* lmdh 12/10/2026 (lot AS, B8 de l audit technique) : au telephone, le choix d une taille durait 400 a 480 ms, dont
     environ 300 ms des applications (Google, Meta) qui surveillent history.replaceState et travaillent DANS l appel.
     L adresse (?variant=) est maintenant posee apres l affichage du nouveau choix : image suivante, puis tache suivante.
     Chaque changement garde son propre appel, dans l ordre, avec sa variante : les applications recoivent les memes
     evenements, quelques millisecondes plus tard. Onglet cache (pas d image) : l adresse suit au plus tard 1 s apres. */
  const apresAffichage = (fn) => {
    let fait = false;
    const lancer = () => {
      if (fait) return;
      fait = true;
      fn();
    };
    requestAnimationFrame(() => window.setTimeout(lancer, 0));
    window.setTimeout(lancer, 1000);
  };

  /* lmdh 30/09/2026 (rapport 334, B2) : tant qu une option (la taille) n etait pas choisie, le bouton de la
     barre collante etait desactive et ne faisait rien, a deux ecrans et demi du choix. Il reste actif :
     il remonte a l option manquante et la signale d un filet. Epuise, il reste desactive. */
  const optionManquante = () =>
    [...document.querySelectorAll('[data-variant-picker] [data-variant-option]')].find((fieldset) => {
      const select = fieldset.querySelector('select');
      if (select) return !select.value;
      const radios = fieldset.querySelectorAll('input[type="radio"]');
      return radios.length > 0 && ![...radios].some((radio) => radio.checked);
    }) || null;

  /* lmdh 07/10/2026 (critique EasyDrap, U48) : la quantite dans le prix de la barre (« 3 × 22,90 € ») ; avec 3 housses,
     la barre disait « 22,90 € ». Tant que la variante n est pas choisie, « Des 14,90 € », sans quantite. */
  const textePrixBarre = (racine) => {
    const prix = (racine || document).querySelector('[data-price-block] .price__amount, [data-price-block] .price .price__amount');
    if (!prix) return '';
    const des = ((racine || document).querySelector('[data-price-block] .price__from')?.textContent || '').trim();
    const champ = document.querySelector('.buy [name="quantity"]');
    const q = Math.max(1, Math.floor(Number(champ?.value) || 1));
    return (des ? des + ' ' : q > 1 ? q + '\u00a0×\u00a0' : '') + prix.textContent.trim();
  };

  /* lmdh 07/10/2026 (critique EasyCover) : un prix long (« Set complet dès 119,90 € ») passe sur deux lignes */
  /* lmdh 12/10/2026 (lot AQ, N2 du balayage final) : sur deux lignes, la barre des 8 EasyCover faisait 75 px au telephone
     au lieu de 74. Un prix long qui porte « dès » se compose : la mention (« Set complet dès ») dans un span, ses mots du
     milieu (« complet ») dans un autre, puis le montant. Au telephone, la mention passe en petit et sans ses mots du
     milieu (« Set dès »), le montant reste en gras : une ligne, comme le bloc prix de la fiche. Sur ordinateur,
     l affichage ne change pas. Sans « dès », la regle des deux lignes demeure. */
  const poserPrixBarre = (el, texte) => {
    const m = texte.length > 16 ? texte.match(/^(\S+)((?:\s+\S+)*?)\s+(dès|desde)\s+(\S.*)$/i) : null;
    if (m) {
      const mention = document.createElement('span');
      mention.className = 'sticky-buy__mention';
      const milieu = document.createElement('span');
      milieu.className = 'sticky-buy__mention-milieu';
      milieu.textContent = m[2];
      mention.append(m[1], milieu, ' ' + m[3]);
      el.replaceChildren(mention, ' ' + m[4]);
      el.classList.remove('is-long');
      el.classList.add('is-mention');
      return;
    }
    el.classList.remove('is-mention');
    el.textContent = texte;
    el.classList.toggle('is-long', texte.length > 16);
  };

  const etatSticky = (sticky, mainButton, libelle) => {
    const bouton = sticky.querySelector('[data-sticky-add]');
    const texte = sticky.querySelector('[data-sticky-label]');
    if (!bouton || !mainButton) return;
    const manque = mainButton.disabled ? optionManquante() : null;
    bouton.disabled = mainButton.disabled && !manque;
    if (manque) bouton.dataset.mode = 'choisir';
    else delete bouton.dataset.mode;
    if (texte) texte.textContent = manque ? mainButton.dataset.labelChoose || libelle : libelle;
  };

  const signalerOption = (fieldset) => {
    fieldset.scrollIntoView({ block: 'center', behavior: reduce.matches ? 'auto' : 'smooth' });
    fieldset.classList.remove('is-a-choisir');
    void fieldset.offsetWidth;
    fieldset.classList.add('is-a-choisir');
    window.setTimeout(() => fieldset.classList.remove('is-a-choisir'), 2400);
    // un choix a tuiles recoit le focus (annonce au lecteur d ecran) ; une liste deroulante non,
    // pour ne pas ouvrir le selecteur natif de l iPhone pendant le defilement
    fieldset.querySelector('input[type="radio"]:not(:disabled)')?.focus({ preventScroll: true });
    // lmdh 06/10/2026 (seconde critique) : « Choisissez une taille… » ne s affiche plus qu ici, quand la cliente demande
    // l ajout sans taille ; au simple choix d un coloris, il se lisait comme une erreur qu elle n avait pas faite.
    const picker = fieldset.closest('[data-variant-picker]');
    const erreur = picker && picker.querySelector('[data-variant-error]');
    if (erreur) {
      let texte = '';
      try {
        texte = JSON.parse(picker.closest('.shopify-section')?.querySelector('[data-variant-data]')?.textContent || '{}').labels?.incomplete || '';
      } catch (e) {
        texte = '';
      }
      if (texte) {
        erreur.textContent = texte;
        erreur.hidden = false;
      }
    }
  };

  /* ----------------------------------------------------------------------
   * <media-gallery>
   * -------------------------------------------------------------------- */

  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      if (!this.track) return;

      this.thumbs = this.querySelector('[data-gallery-thumbs]');
      this.items = [...this.querySelectorAll('[data-media-id]')];
      this.prevBtn = this.querySelector('[data-gallery-prev]');
      this.nextBtn = this.querySelector('[data-gallery-next]');
      this.thumbsPrev = this.querySelector('[data-thumbs-prev]');
      this.thumbsNext = this.querySelector('[data-thumbs-next]');

      this.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-gallery-thumb]');
        if (thumb) {
          this.select(thumb.dataset.galleryThumb);
          this.announce(thumb.dataset.galleryThumb);
          return;
        }

        if (event.target.closest('[data-gallery-prev]')) {
          this.step(-1);
          return;
        }

        if (event.target.closest('[data-gallery-next]')) {
          this.step(1);
          return;
        }

        if (event.target.closest('[data-thumbs-prev]')) {
          this.scrollThumbs(-1);
          return;
        }

        if (event.target.closest('[data-thumbs-next]')) {
          this.scrollThumbs(1);
          return;
        }

        const zoom = event.target.closest('[data-zoom]');
        if (zoom) this.openZoom(zoom.dataset.zoom, zoom.querySelector('img')?.alt || '');
      });

      this.observer = new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
          if (visible) this.markActive(visible.target.dataset.mediaId);
        },
        { root: this.track, threshold: 0.6 }
      );

      this.items.forEach((item) => this.observer.observe(item));

      // lmdh 12/10/2026 (lot AS3, B4) : les autres photos du coloris affiche (data-lmdh-apres) se construisent une fois la
      // photo principale chargee, ou des que la galerie est touchee (doigt, souris, clavier, defilement de la piste)
      this.setupApres();

      // lmdh 12/10/2026 (lot AS, B1) : position des deux pistes, relue a chaque defilement (mise en page a jour a ce
      // moment-la) ; null = inconnue (defilement programme en cours) : lead() ne saute alors pas son scrollTo.
      this.gauchePiste = 0;
      this.gaucheVignettes = 0;
      this.track.addEventListener('scroll', () => {
        this.gauchePiste = this.track.scrollLeft;
        this.updateNav();
      }, { passive: true });
      this.thumbs?.addEventListener('scroll', () => {
        this.gaucheVignettes = this.thumbs.scrollLeft;
        this.updateThumbsNav();
      }, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });

      this.updateNav();
      this.updateThumbsNav();
    }

    onResize = () => {
      this.updateNav();
      this.updateThumbsNav();
    };

    disconnectedCallback() {
      this.observer?.disconnect();
      window.removeEventListener('resize', this.onResize);
    }

    /* lmdh 12/10/2026 (lot AS3, B4) : voir connectedCallback. lancer() ne sert qu une fois ; une photo d un coloris cache
       attend que son coloris s affiche (showGroup), comme avant. */
    setupApres() {
      if (!this.items.some((el) => el.dataset.lmdhApres !== undefined && el.dataset.lmdhSrc)) {
        this.apresPret = true;
        return;
      }
      const lancer = () => {
        if (this.apresPret) return;
        this.apresPret = true;
        this.items.forEach((el) => {
          if (!el.hidden && el.dataset.lmdhApres !== undefined) this.hydrate(el);
        });
      };
      ['pointerdown', 'touchstart', 'keydown', 'focusin', 'mouseover'].forEach((type) =>
        this.addEventListener(type, lancer, { once: true, passive: true })
      );
      this.track.addEventListener('scroll', lancer, { once: true, passive: true });
      const principale = this.track.querySelector('img');
      if (!principale || principale.complete) {
        requestAnimationFrame(() => window.setTimeout(lancer, 0));
        return;
      }
      principale.addEventListener('load', () => window.setTimeout(lancer, 0), { once: true });
      principale.addEventListener('error', lancer, { once: true });
    }

    /* lmdh : seules les photos du coloris courant (et les visuels hors groupe) comptent */
    get visibleItems() {
      return this.items.filter((item) => !item.hidden);
    }

    activeIndex() {
      const items = this.visibleItems;
      return Math.max(
        0,
        items.findIndex((item) => item.hasAttribute('data-active'))
      );
    }

    step(delta) {
      const items = this.visibleItems;
      const next = Math.min(items.length - 1, Math.max(0, this.activeIndex() + delta));
      const item = items[next];
      if (item) {
        this.select(item.dataset.mediaId);
        this.announce(item.dataset.mediaId);
      }
    }

    /**
     * lmdh : ne garde visibles que les medias du groupe (alt de la photo de
     * variante) et ceux qui n appartiennent a aucun groupe de variante.
     * @param {string} group  alt de reference du coloris courant
     * @param {Set<string>} known  tous les alts de variantes du produit
     */
    showGroup(group, known) {
      if (!group) return;
      const apply = (el) => {
        const g = el.dataset.lmdhGroup || '';
        el.hidden = Boolean(g) && known.has(g) && g !== group;
        // lmdh 12/10/2026 (lot AS3) : les photos « apres » du coloris d arrivee attendent la photo principale (setupApres)
        if (!el.hidden && (this.apresPret || el.dataset.lmdhApres === undefined)) this.hydrate(el);
      };
      this.items.forEach(apply);
      this.querySelectorAll('[data-gallery-thumbs] > li').forEach(apply);
      const wrap = this.querySelector('[data-thumbs-wrap]');
      if (wrap) wrap.hidden = this.visibleItems.length < 2;
      this.updateNav();
      // lmdh 12/10/2026 (lot AS, B1) : les fleches des vignettes lisent la largeur de la bande (mise en page forcee de 70 a
      // 90 ms au telephone, au milieu du choix d un coloris) ; relues a l image suivante, une fois la page dessinee
      requestAnimationFrame(() => this.updateThumbsNav());
    }

    /* lmdh : previent le selecteur qu une photo a ete choisie (son groupe = son coloris) */
    announce(mediaId) {
      const item = this.querySelector(`[data-media-id="${mediaId}"]`);
      if (!item) return;
      this.dispatchEvent(
        new CustomEvent('lmdh:media', { bubbles: true, detail: { group: item.dataset.lmdhGroup || '' } })
      );
    }

    scrollThumbs(direction) {
      if (!this.thumbs) return;
      const amount = Math.max(160, this.thumbs.clientWidth * 0.7) * direction;
      this.thumbs.scrollBy({
        left: amount,
        behavior: reduce.matches ? 'auto' : 'smooth',
      });
    }

    select(mediaId) {
      const item = this.querySelector(`[data-media-id="${mediaId}"]`);
      if (!item) return;

      // lmdh 28/09/2026 : defile la piste seulement. scrollIntoView(block: 'nearest') faisait
      // remonter toute la page d environ 300 px a chaque choix de coloris ou de taille (mobile).
      this.scrollWithin(this.track, item);

      this.markActive(mediaId);
    }

    /**
     * lmdh 01/10/2026 : la photo de la variante passe en tete de la piste et des miniatures, et devient
     * la photo affichee. Les vues importees d un coloris la precedaient (gros plan du velours, canape sur la
     * fiche fauteuil). L ordre des photos de la fiche ne change pas : seul l affichage est reordonne.
     */
    lead(mediaId) {
      const id = String(mediaId);
      const item = this.track.querySelector(`[data-media-id="${id}"]`);
      if (!item) return;
      if (this.track.firstElementChild !== item) this.track.prepend(item);
      const thumb = this.thumbs?.querySelector(`[data-gallery-thumb="${id}"]`)?.closest('li');
      if (thumb && this.thumbs.firstElementChild !== thumb) this.thumbs.prepend(thumb);
      this.items = [...this.querySelectorAll('[data-media-id]')];
      // lmdh 12/10/2026 (lot AS, B1) : deja au debut (cas courant : la piste n a pas ete touchee), pas de scrollTo, qui
      // forcait une mise en page (40 a 60 ms au telephone)
      if (this.gauchePiste !== 0) this.track.scrollTo({ left: 0, top: 0, behavior: 'auto' });
      if (this.thumbs && this.gaucheVignettes !== 0) this.thumbs.scrollTo({ left: 0, behavior: 'auto' });
      this.markActive(id);
    }

    /**
     * lmdh 03/10/2026 (rapport 346, T2) : les photos et vignettes des coloris caches au chargement arrivent sans
     * image (data-lmdh-src, data-lmdh-alt, data-lmdh-w/h, data-lmdh-sizes) ; on la construit quand on en a besoin,
     * avec les memes tailles que le rendu serveur, et l icone de zoom des grandes photos.
     * @param {Element} el  .gallery__item ou li de vignette
     */
    hydrate(el) {
      const src = el && el.dataset.lmdhSrc;
      if (!src) return;
      const host = el.querySelector('button') || el;
      const img = document.createElement('img');
      img.alt = el.dataset.lmdhAlt || '';
      img.loading = 'lazy';
      img.decoding = 'async';
      if (el.dataset.lmdhW) img.width = Number(el.dataset.lmdhW);
      if (el.dataset.lmdhH) img.height = Number(el.dataset.lmdhH);
      if (el.dataset.lmdhSizes) {
        img.sizes = el.dataset.lmdhSizes;
        img.srcset = [400, 600, 800, 1000, 1200]
          .map((w) => `${src.replace(/([?&])width=\d+/, `$1width=${w}`)} ${w}w`)
          .join(', ');
      }
      img.src = src;
      host.prepend(img);
      if (host.classList.contains('gallery__frame') && !host.querySelector('.gallery__zoom-hint')) {
        const hint = this.querySelector('.gallery__zoom-hint');
        if (hint) host.appendChild(hint.cloneNode(true));
      }
      delete el.dataset.lmdhSrc;
    }

    /* lmdh 01/10/2026 : source de la photo affichee pour un media (vignette du coloris) */
    sourceOf(mediaId) {
      this.hydrate(this.querySelector(`[data-media-id="${mediaId}"]`));
      const img = this.querySelector(`[data-media-id="${mediaId}"] img`);
      return img ? img.currentSrc || img.src : '';
    }

    markActive(mediaId) {
      const id = String(mediaId);

      this.querySelectorAll('[data-media-id]').forEach((item) => {
        item.toggleAttribute('data-active', item.dataset.mediaId === id);
      });

      let aCentrer = null;
      this.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
        const active = thumb.dataset.galleryThumb === id;
        if (active) {
          thumb.setAttribute('aria-current', 'true');
          aCentrer = thumb;
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
      // lmdh 12/10/2026 (lot AS, B1) : la vignette active se centre a l image suivante (sa lecture de geometrie forcait
      // une mise en page au milieu du choix)
      if (aCentrer) requestAnimationFrame(() => this.scrollWithin(this.thumbs, aCentrer));

      this.updateNav();
    }

    /* lmdh 28/09/2026 : centre un element dans son conteneur defilant, sans toucher a la page */
    scrollWithin(container, element) {
      if (!container || !element) return;
      const box = container.getBoundingClientRect();
      const rect = element.getBoundingClientRect();
      const left = container.scrollLeft + (rect.left - box.left) - (container.clientWidth - rect.width) / 2;
      const top = container.scrollTop + (rect.top - box.top) - (container.clientHeight - rect.height) / 2;
      // lmdh 12/10/2026 (lot AS, B1) : un defilement qui ne bougerait rien n est pas lance ; sinon la position de la
      // piste devient inconnue (null) jusqu a son evenement scroll, et lead() ne saute plus son scrollTo
      const gauche = Math.max(0, Math.min(container.scrollWidth - container.clientWidth, left));
      const haut = Math.max(0, Math.min(container.scrollHeight - container.clientHeight, top));
      if (Math.abs(gauche - container.scrollLeft) < 1 && Math.abs(haut - container.scrollTop) < 1) return;
      if (container === this.track) this.gauchePiste = null;
      if (container === this.thumbs) this.gaucheVignettes = null;
      container.scrollTo({
        left,
        top,
        behavior: reduce.matches ? 'auto' : 'smooth',
      });
    }

    updateNav() {
      const items = this.visibleItems;
      if (!items.length) return;

      const index = this.activeIndex();
      const multi = items.length > 1;

      if (this.prevBtn) this.prevBtn.hidden = !multi || index <= 0;
      if (this.nextBtn) this.nextBtn.hidden = !multi || index >= items.length - 1;
    }

    updateThumbsNav() {
      if (!this.thumbs) return;

      const { scrollLeft, scrollWidth, clientWidth } = this.thumbs;
      const overflow = scrollWidth > clientWidth + 4;

      if (this.thumbsPrev) {
        this.thumbsPrev.hidden = !overflow || scrollLeft <= 4;
      }
      if (this.thumbsNext) {
        this.thumbsNext.hidden = !overflow || scrollLeft + clientWidth >= scrollWidth - 4;
      }
    }

    openZoom(src, alt) {
      const dialog = document.getElementById('gallery-zoom');
      const image = dialog?.querySelector('[data-zoom-target]');
      if (!dialog || !image) return;

      image.src = src;
      image.alt = alt;
      dialog.showModal();
    }
  }

  /* ----------------------------------------------------------------------
   * Selecteur de variantes
   *
   * La combinaison choisie est resolue en local a partir des donnees
   * serialisees. Le prix, lui, est redemande au serveur : c'est le seul
   * moyen de garder le formatage monetaire, les remises et les prix
   * a l'unite coherents avec le reste du site.
   * -------------------------------------------------------------------- */

  class VariantPicker {
    constructor(root) {
      this.root = root;
      this.section = root.closest('.shopify-section');
      if (!this.section) return;

      const payload = this.section.querySelector('[data-variant-data]');
      if (!payload) return;

      try {
        this.data = JSON.parse(payload.textContent);
      } catch (error) {
        return;
      }

      this.controller = null;
      // lmdh : a connaitre AVANT le premier refreshAvailability, sinon les coloris
      // hors JSON (> 250 variantes) sont barres au chargement.
      this.truncated = Number(this.data.variantsCount || 0) > this.data.variants.length;
      this.remote = new Map();
      this.root.addEventListener('change', () => this.onChange());
      this.refreshAvailability();

      // lmdh : galerie groupee par coloris. Les groupes (data-lmdh-group, poses
      // par product-media.liquid) sont des NOMS DE COLORIS ; colorGroups (Liquid,
      // value.variant) donne la photo de chaque coloris, meme au-dela de 250 variantes.
      this.colorIndex = Number(this.data.colorPosition || 0) - 1;
      this.colorGroups = this.data.colorGroups || {};
      this.knownGroups = new Set(Object.keys(this.colorGroups));
      if (this.colorIndex >= 0) {
        this.syncGallery(this.selection, this.find(this.selection));
        this.section.querySelector('[data-gallery]')?.addEventListener('lmdh:media', (event) => {
          const color = event.detail?.group || '';
          if (!this.knownGroups.has(color) || color === this.selection[this.colorIndex]) return;
          this.setOption(this.colorIndex, color);
        });
      }

      const lmdhTailleRetiree = this.dropImposedSize();

      // Quand la taille n'est pas precochee, le bouton doit partir desactive :
      // sans cela il resterait cliquable jusqu'au premier changement.
      if (this.selection.some((value) => value === undefined)) {
        this.applyIncomplete();
        if (lmdhTailleRetiree) this.showColorFrom();
      } else {
        this.syncSticky();
      }
      this.syncTilePrices();
    }

    /**
     * lmdh 28/09/2026 (critique Impeccable) : depuis une collection filtree ou une recherche,
     * Shopify ajoute ?variant= (la premiere variante du coloris filtre, donc « Une Place ») avec
     * _pos. On garde le coloris, on laisse la taille a choisir : personne n a choisi « Une Place ».
     */
    dropImposedSize() {
      const params = new URLSearchParams(window.location.search);
      // _pos : collection filtree ou recherche Shopify ; lmdh=coloris : liens « coloris » du panier
      if (!params.has('variant') || !(params.has('_pos') || params.get('lmdh') === 'coloris')) return;
      // lmdh 10/10/2026 (rapport 375, U57) : un format impose aussi (EasyCover « Set Complet - Fauteuil », jetes, coussins)
      const sizeIndex = (this.data.options || []).findIndex((name) => /taille|talla|size|format|tamaño/i.test(String(name || '')));
      if (sizeIndex < 0 || (this.data.optionCount ?? 0) < 2) return;
      const radios = this.root.querySelectorAll(`input[type="radio"][data-option-position="${sizeIndex + 1}"]`);
      // une option a une seule valeur reste cochee : il n y a rien a choisir
      if (radios.length < 2) return;
      radios.forEach((radio) => {
        radio.checked = false;
      });
      this.root.querySelectorAll(`[data-option-selected="${sizeIndex + 1}"]`).forEach((element) => {
        element.textContent = '';
      });
      this.refreshAvailability();
      params.delete('variant');
      params.delete('lmdh');
      const query = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (query ? `?${query}` : '') + window.location.hash);
      return true;
    }

    /**
     * lmdh 04/10/2026 (rapport 346, M5 et U4) : coloris choisi, taille pas encore. Le prix varie selon le coloris
     * (deperlante : Gris 49,90 EUR, Cafe 59,90 EUR en 1 place) : le bloc prix est rendu par le serveur pour la variante
     * la moins chere DU COLORIS, et dit « Dès » si ses tailles ont des prix differents. Seulement quand l option
     * manquante est la taille : les formats (EasyCover, coussins, jetes) gardent leur logique.
     */
    showColorFrom() {
      if (this.colorIndex < 0) return;
      const values = this.selection;
      const sizeIndex = (this.data.options || []).findIndex((name) => /taille|talla|size/i.test(String(name || '')));
      if (sizeIndex < 0 || values[sizeIndex] !== undefined) return;
      if (values.some((value, index) => index !== sizeIndex && value === undefined)) return;
      const color = values[this.colorIndex];
      const candidats = (this.data.variants || []).filter((v) => v.options[this.colorIndex] === color);
      // toutes les tailles du coloris doivent etre dans le JSON (plafond Liquid de 250 variantes), et chacune avec son prix
      const tailles = this.root.querySelectorAll(`input[type="radio"][data-option-position="${sizeIndex + 1}"]`).length;
      if (!candidats.length || candidats.length < tailles || candidats.some((v) => typeof v.price !== 'number')) {
        // lmdh 06/10/2026 (critique, P1) : coloris au-dela des 250 variantes du JSON : ses tailles demandees au serveur
        if (this.truncated) {
          this.completerTailles(sizeIndex).then((ajout) => {
            if (ajout) this.showColorFrom();
          });
        }
        // coloris incomplet dans le JSON : on revient au prix de la fiche si un prix de coloris etait affiche
        if (this.colorFromKey && this.colorFromKey !== 'fiche') {
          this.colorFromKey = 'fiche';
          const fiche = new URL(this.data.url, window.location.origin);
          fiche.searchParams.delete('variant');
          this.refreshPrice(fiche).finally(() => this.syncSticky());
        }
        return;
      }
      const dispo = candidats.filter((v) => v.available);
      const moinsCher = (dispo.length ? dispo : candidats).reduce((x, y) => (y.price < x.price ? y : x));
      const plusieurs = new Set(candidats.map((v) => v.price)).size > 1;
      const key = color + '\u0000' + moinsCher.id;
      if (this.colorFromKey === key) return;
      this.colorFromKey = key;
      const url = new URL(this.data.url, window.location.origin);
      url.searchParams.set('variant', String(moinsCher.id));
      this.refreshPrice(url)
        .then(() => {
          if (this.colorFromKey !== key) return;
          const prix = this.section.querySelector('[data-price-block] .price');
          if (plusieurs && prix && !prix.querySelector('.price__from')) {
            const des = document.createElement('span');
            des.className = 'price__from';
            des.textContent = this.data.labels?.from || 'Desde';
            prix.prepend(des);
          }
        })
        .finally(() => this.syncSticky());
    }

    /**
     * lmdh 06/10/2026 (critique, P1) : le prix de chaque tuile de taille suit les autres options choisies (le coloris).
     * Le serveur rend le prix le plus bas de la taille, tous coloris confondus : avec un motif (+5 EUR sur l'extensible),
     * la tuile disait « dès 44,90 € » quand le prix du coloris choisi etait 49,90 €. Le montant est recompose dans le
     * texte du serveur (devise et espaces gardes). Combinaison absente du JSON (plafond Liquid de 250 variantes) :
     * completerTailles la demande au serveur ; la tuile garde le texte du serveur en attendant.
     */
    syncTilePrices() {
      const sizeIndex = (this.data.options || []).findIndex((name) => /taille|talla|size/i.test(String(name || '')));
      if (sizeIndex < 0) return;
      const values = this.selection;
      const autres = values.some((value, index) => index !== sizeIndex && value !== undefined);
      const source = this.data.variants || [];
      let manque = false;
      this.root.querySelectorAll(`input[type="radio"][data-option-position="${sizeIndex + 1}"]`).forEach((input) => {
        const span = this.root.querySelector(`label[for="${CSS.escape(input.id)}"] .variant__tile-price`);
        if (!span) return;
        if (span.dataset.serveur === undefined) span.dataset.serveur = span.textContent.trim();
        const serveur = span.dataset.serveur;
        const prix = autres
          ? source
              .filter((v) => v.options[sizeIndex] === input.value && v.options.every((o, i) => i === sizeIndex || values[i] === undefined || o === values[i]))
              .map((v) => v.price)
              .filter((p) => typeof p === 'number')
          : [];
        const montant = serveur.match(/\d{1,3}(?:[\s\u00a0\u202f.]\d{3})*,\d{2}/);
        if (!prix.length || !montant) {
          if (autres && !prix.length) manque = true;
          span.textContent = serveur;
          return;
        }
        const min = Math.min(...prix);
        const max = Math.max(...prix);
        const texte = serveur.replace(/^\s*(dès|desde)\s*/i, '').replace(montant[0], (min / 100).toFixed(2).replace('.', ','));
        span.textContent = (max > min ? 'desde ' : '') + texte;
      });
      if (manque && this.truncated) {
        this.completerTailles(sizeIndex).then((ajout) => {
          if (!ajout) return;
          this.syncTilePrices();
          this.showColorFrom();
        });
      }
    }

    /**
     * lmdh 06/10/2026 (critique, P1) : au-dela de 250 variantes, le JSON de la page (et /products/<handle>.js, plafonne
     * lui aussi a 250) ne contient pas les derniers coloris. Chaque taille manquante du coloris choisi est demandee au
     * serveur par option_values, comme resolveRemote, puis rangee dans data.variants. Rend vrai si une variante a ete
     * ajoutee (sinon personne ne relance : pas de boucle sur un echec).
     */
    async completerTailles(sizeIndex) {
      const values = this.selection;
      const ids = new Array(values.length).fill(undefined);
      this.root.querySelectorAll('input[type="radio"]:checked').forEach((el) => {
        ids[Number(el.dataset.optionPosition) - 1] = el.dataset.optionValueId;
      });
      this.root.querySelectorAll('select[data-option-position]').forEach((el) => {
        ids[Number(el.dataset.optionPosition) - 1] = el.selectedOptions[0]?.dataset.optionValueId;
      });
      const sectionId = this.section.id.replace('shopify-section-', '');
      const radios = [...this.root.querySelectorAll(`input[type="radio"][data-option-position="${sizeIndex + 1}"]`)];
      const resultats = await Promise.all(
        radios.map(async (input) => {
          const cible = [...values];
          cible[sizeIndex] = input.value;
          if (cible.some((value) => value === undefined)) return false;
          const key = cible.join('\u0000');
          if (this.find(cible) || this.remote.has(key)) return false;
          const idsCible = [...ids];
          idsCible[sizeIndex] = input.dataset.optionValueId;
          if (idsCible.some((id) => !id)) return false;
          this.remote.set(key, null);
          try {
            const url = new URL(this.data.url, window.location.origin);
            url.searchParams.set('section_id', sectionId);
            url.searchParams.set('option_values', idsCible.join(','));
            const response = await fetch(url);
            if (!response.ok) {
              this.remote.delete(key);
              return false;
            }
            const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
            const payload = doc.querySelector('[data-variant-data]');
            const found = payload && JSON.parse(payload.textContent).currentVariant;
            if (!found || typeof found.price !== 'number' || !found.options.every((option, index) => option === cible[index])) return false;
            if (!this.find(cible)) this.data.variants.push(found);
            this.remote.set(key, found);
            return true;
          } catch (error) {
            this.remote.delete(key);
            return false;
          }
        })
      );
      return resultats.some(Boolean);
    }

    /* lmdh : montre les photos du coloris choisi, meme sans taille choisie */
    syncGallery(values, variant) {
      if (this.colorIndex < 0) return;
      const gallery = this.section.querySelector('[data-gallery]');
      if (!gallery || typeof gallery.showGroup !== 'function') return;
      const color = values[this.colorIndex];
      if (color === undefined || !this.knownGroups.has(color)) return;
      // lmdh 12/10/2026 (lot AS, B1 de l audit technique) : les photos ne se retrient que si le coloris change. Au choix
      // d une taille, showGroup reposait hidden sur toutes les photos et vignettes (494 sur l extensible) et relisait la
      // geometrie de la galerie : 85 a 135 ms au telephone, pour un affichage identique.
      if (color !== this.groupeAffiche) {
        gallery.showGroup(color, this.knownGroups);
        this.groupeAffiche = color;
      }
      const mediaId = (variant && variant.media_id) || (this.colorGroups[color] || {}).media_id;
      // lmdh 01/10/2026 : a chaque nouveau coloris, sa photo de variante passe en tete et s affiche
      // (avant : seulement quand la taille manquait, et sans la remettre en tete).
      if (mediaId && color !== this.ledColor) {
        this.ledColor = color;
        gallery.lead(mediaId);
      } else if (!variant && mediaId) {
        gallery.select(mediaId);
      }
      // lmdh 12/10/2026 (lot AS, B1) : la vignette du coloris ne se recharge que si sa photo change
      const cleApercu = String(mediaId || '');
      if (cleApercu !== this.apercuAffiche) {
        this.apercuAffiche = cleApercu;
        this.updatePreview(gallery, mediaId);
      }
    }

    /* lmdh 01/10/2026 : vignette du coloris a cote de « Couleur : … » (telephone) ; elle suit le coloris
       choisi, et un toucher remonte a la grande photo. */
    updatePreview(gallery, mediaId) {
      const preview = this.root.querySelector('[data-color-preview]');
      if (!preview) return;
      const img = preview.querySelector('img');
      const src = mediaId ? gallery.sourceOf(mediaId) : '';
      if (img && src) {
        // lmdh 03/10/2026 (rapport 346, T6) : la vignette fait 56 px ; on demande la meme photo en 168 px au lieu de
        // recopier la grande image de la galerie.
        const petit = /[?&]width=\d+/.test(src)
          ? src.replace(/([?&])width=\d+/, '$1width=168')
          : src + (src.includes('?') ? '&' : '?') + 'width=168';
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.src = petit;
      }
      if (!preview.dataset.ready) {
        preview.dataset.ready = 'true';
        preview.addEventListener('click', () => {
          const top = gallery.getBoundingClientRect().top + window.scrollY;
          const header = document.querySelector('.header, header')?.getBoundingClientRect().height || 0;
          window.scrollTo({ top: Math.max(0, top - header - 8), behavior: reduce.matches ? 'auto' : 'smooth' });
        });
      }
    }

    /**
     * lmdh : combinaison absente du JSON sur un produit tronque (> 250 variantes).
     * La section est rechargee avec option_values=<ids> ; Shopify y place la
     * variante correspondante dans product.selected_or_first_available_variant,
     * que le JSON expose en "currentVariant". Mise en cache, puis onChange rejoue.
     */
    async resolveRemote(values) {
      const key = values.join('\u0000');
      if (this.remote.has(key)) return;
      this.remote.set(key, null);
      const ids = [];
      this.root.querySelectorAll('input[type="radio"]:checked, select[data-option-position]').forEach((el) => {
        const id = el.tagName === 'SELECT' ? el.selectedOptions[0]?.dataset.optionValueId : el.dataset.optionValueId;
        if (id) ids.push(id);
      });
      if (ids.length !== values.length) return;
      const sectionId = this.section.id.replace('shopify-section-', '');
      const url = new URL(this.data.url, window.location.origin);
      url.searchParams.set('section_id', sectionId);
      url.searchParams.set('option_values', ids.join(','));
      try {
        const response = await fetch(url);
        if (!response.ok) return;
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const payload = doc.querySelector('[data-variant-data]');
        const found = payload && JSON.parse(payload.textContent).currentVariant;
        if (!found || !found.options.every((option, index) => option === values[index])) return;
        this.data.variants.push(found);
        this.remote.set(key, found);
        if (this.selection.join('\u0000') === key) this.onChange();
      } catch (error) {
        this.remote.delete(key);
      }
    }

    /* lmdh : coche une valeur d option (radio ou select) puis rejoue le changement */
    setOption(index, value) {
      const position = String(index + 1);
      const radio = [...this.root.querySelectorAll(`input[type="radio"][data-option-position="${position}"]`)].find(
        (input) => input.value === value
      );
      let target = radio;
      if (radio) {
        radio.checked = true;
        radio.closest('.variant__options')?.classList.remove('is-collapsed');
      } else {
        const select = this.root.querySelector(`select[data-option-position="${position}"]`);
        if (!select) return;
        select.value = value;
        target = select;
      }
      // un vrai evenement change : le selecteur ET les autres blocs (coloris partages) se mettent a jour
      target.dispatchEvent(new Event('change', { bubbles: true }));
    }

    applyIncomplete() {
      const button = this.section.querySelector('[data-add-button]');
      const label = this.section.querySelector('[data-add-label]');
      const idInput = this.section.querySelector('[data-variant-id]');
      if (button) {
        button.disabled = false;
        button.dataset.mode = 'choisir';
      }
      if (label && button?.dataset.labelChoose) {
        label.textContent = button.dataset.labelChoose;
      }
      if (idInput) {
        idInput.value = '';
        idInput.setAttribute('disabled', 'disabled');
      }
      const error = this.section.querySelector('[data-variant-error]');
      if (error) {
        error.hidden = true;
      }
      this.syncSticky();
    }

    syncSticky() {
      const sticky = document.querySelector('[data-sticky-buy]');
      if (!sticky) return;

      const mainButton = this.section.querySelector('[data-add-button]');
      const stickyButton = sticky.querySelector('[data-sticky-add]');
      const stickyLabel = sticky.querySelector('[data-sticky-label]');
      const stickyPrice = sticky.querySelector('[data-sticky-price]');
      const stickyVariant = sticky.querySelector('[data-sticky-variant]');
      const priceBlock = this.section.querySelector(
        '[data-price-block] .price__amount, [data-price-block] .price .price__amount'
      );

      // lmdh 03/10/2026 (rapport 346, U4) : tant qu aucune taille n est choisie, la barre dit « Dès 34,90 € »
      // comme le bloc prix (le « Dès » vit dans un autre span que le montant).
      if (stickyPrice && priceBlock) {
        poserPrixBarre(stickyPrice, textePrixBarre(this.section));
      }

      if (stickyVariant) {
        const values = this.selection;
        // lmdh 07/10/2026 (critique EasyDrap, U48) : « Format » compte comme une taille ; la barre disait le coloris
        const sizeIndex = (this.data.options || []).findIndex((name) =>
          /taille|talla|size|format|tamaño/i.test(String(name || ''))
        );
        const estFormat = sizeIndex >= 0 && /format|tamaño/i.test(String((this.data.options || [])[sizeIndex] || ''));
        // lmdh 28/09/2026 : sans option de taille, on nomme l option reelle (« Couleur : Gris »),
        // jamais « Taille : Kaki » ni « Choisir une taille » sur une chaise.
        const sizeValue = sizeIndex >= 0 ? values[sizeIndex] : undefined;
        const firstName = (this.data.options || [])[0];

        // lmdh 30/09/2026 (rapport 334, B13) : la valeur seule, sans « Taille : » ni la plage en cm, qui
        // coupaient le libelle a 110 px (« Taille : Trois Plac… ») ; la plage reste sur les tuiles.
        if (sizeValue) {
          // lmdh 07/10/2026 (critique EasyCover) : « Set Complet - Canapé 3 places » -> « Set canapé 3 places » ;
          // « Pièce à l'unité : 70x150cm » -> « Pièce 70 × 150 »
          const court = String(sizeValue).replace(/\s*\([^)]*\)\s*$/, '').trim()
            .replace(/^juego completo\s*-\s*(.+)$/i, (m, r) => 'Juego ' + r.charAt(0).toLowerCase() + r.slice(1))
            .replace(/\b1 places\b/, '1 plaza')
            .replace(/^pieza suelta\s*:\s*(\d+)\s*x\s*(\d+)\s*cm$/i, 'Pieza $1\u00a0\u00d7\u00a0$2');
          // lmdh 08/10/2026 (critique des jetes) : « Canapé XL : jusqu’à 420… » tronque, sans coloris ni « cm » -> la
          // dimension et le coloris, « 180 × 420 cm · Noir rameaux »
          const jete = String(sizeValue).match(/cubresof[aá]\s+(\d+)\s*[×x]\s*(\d+)\s*cm/i);
          const colIndex = (this.data.options || []).findIndex((name) => /couleur|coloris|color/i.test(String(name || '')));
          if (jete) {
            const nb = String.fromCharCode(160);
            stickyVariant.textContent = jete[1] + nb + '×' + nb + jete[2] + nb + 'cm' + (colIndex >= 0 && values[colIndex] ? ' · ' + values[colIndex] : '');
          } else if (estFormat && /^\d+\s*[x\u00d7]\s*\d+\s*cm$/i.test(court) && colIndex >= 0 && values[colIndex]) {
            // lmdh 09/10/2026 (critique des coussins) : « 30 × 50 cm · Gris » au lieu de « 30 x 50 »
            const nb = String.fromCharCode(160);
            stickyVariant.textContent = court.replace(/\s*[x\u00d7]\s*/i, nb + '\u00d7' + nb).replace(/\s*cm$/i, nb + 'cm') + ' \u00b7 ' + values[colIndex];
          } else {
            stickyVariant.textContent = estFormat ? court.replace(/\s*cm$/i, '') : court;
          }
        } else if (sizeIndex < 0 || estFormat) {
          // format pas encore choisi : le coloris, comme avant (le bouton dit deja « Choisir un format »)
          stickyVariant.textContent =
            firstName && firstName !== 'Title' && values[0] !== undefined ? String(values[0]) : '';
        } else {
          // lmdh 12/10/2026 (lot AO, U31) : la taille manque ; le bouton de la barre dit deja « Choisir une taille », la
          // ligne du dessous le redisait. Elle nomme le coloris choisi (rien sans option de coloris).
          const colChoisi = (this.data.options || []).findIndex((name) => /couleur|coloris|color/i.test(String(name || '')));
          stickyVariant.textContent = colChoisi >= 0 && values[colChoisi] !== undefined ? String(values[colChoisi]) : '';
        }
      }

      majTotal();
      if (stickyButton && mainButton) {
        etatSticky(
          sticky,
          mainButton,
          this.section.querySelector('[data-add-label]')?.textContent ||
            mainButton.dataset.labelAvailable ||
            stickyLabel?.textContent ||
            ''
        );
      }
    }

    /**
     * Une case par option, indexee sur sa position. Une case vide signifie
     * que le client n'a pas encore choisi — ce n'est pas la meme chose qu'une
     * combinaison inexistante, et le message affiche differe.
     */
    get selection() {
      const values = new Array(this.data.optionCount ?? 0).fill(undefined);

      this.root.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
        values[Number(input.dataset.optionPosition) - 1] = input.value;
      });

      this.root.querySelectorAll('select[data-option-position]').forEach((select) => {
        const value = select.value;
        values[Number(select.dataset.optionPosition) - 1] = value === '' ? undefined : value;
      });

      return values;
    }

    find(values) {
      if (values.some((value) => value === undefined)) return undefined;

      return this.data.variants.find(
        (variant) =>
          variant.options.length === values.length &&
          variant.options.every((option, index) => option === values[index])
      );
    }

    /* lmdh : vrai quand la combinaison peut exister hors du JSON tronque */
    unknown(values) {
      return Boolean(this.truncated) && !this.find(values) && !this.remote.has(values.join('\u0000'));
    }

    onChange() {
      const values = this.selection;
      const variant = this.find(values);
      const incomplete = values.some((value) => value === undefined);
      const pending = !incomplete && !variant && this.unknown(values);
      if (pending) this.resolveRemote(values);
      if (!incomplete) this.colorFromKey = null;

      this.root.querySelectorAll('[data-option-selected]').forEach((element) => {
        const position = Number(element.dataset.optionSelected);
        // lmdh 29/09/2026 : « 90-140 CM » s'affiche « 90-140 cm » (la valeur d'option ne change pas)
        element.textContent = String(values[position - 1] ?? '').replace(/ CM\b/g, ' cm');
      });

      this.refreshAvailability();
      this.syncGallery(values, variant);
      this.syncTilePrices();

      const error = this.root.querySelector('[data-variant-error]');
      if (error) {
        // lmdh 06/10/2026 (seconde critique) : taille manquante = aucun message ici ; signalerOption l affiche au toucher
        // de « Choisir une taille ». Reste le message d une combinaison qui n existe pas.
        const message = incomplete || pending
          ? ''
          : variant
            ? ''
            : this.data.labels?.unavailable;
        error.textContent = message || '';
        error.hidden = !message;
      }

      const button = this.section.querySelector('[data-add-button]');
      const label = this.section.querySelector('[data-add-label]');

      if (!variant) {
        const idInput = this.section.querySelector('[data-variant-id]');
        if (idInput) {
          idInput.value = '';
          idInput.setAttribute('disabled', 'disabled');
        }
        if (button) {
          /* lmdh 05/10/2026 (rapport 351, U23) : taille manquante = bouton actif « Choisir une taille », qui remonte a
             l option et la signale, comme la barre collante. Desactive seulement si la combinaison n existe pas. */
          button.disabled = !incomplete;
          if (incomplete) button.dataset.mode = 'choisir';
          else delete button.dataset.mode;
          button.removeAttribute('data-state');
          if (label) {
            label.textContent = incomplete || pending
              ? button.dataset.labelChoose || button.dataset.labelAvailable
              : button.dataset.labelSoldOut;
          }
        }
        this.syncSticky();
        if (incomplete) this.showColorFrom();
        return;
      }

      const input = this.section.querySelector('[data-variant-id]');
      if (input) {
        input.value = String(variant.id);
        input.removeAttribute('disabled');
      }

      if (button) {
        button.disabled = !variant.available;
        delete button.dataset.mode;
        button.removeAttribute('data-state');
      }
      if (label && button) {
        label.textContent = variant.available
          ? button.dataset.labelAvailable
          : button.dataset.labelSoldOut;
      }

      // lmdh 12/10/2026 (lot AS, B1) : la photo de la variante deja affichee (meme coloris, ou mise en tete par
      // syncGallery) n est pas reselectionnee : select() relisait la geometrie de la piste et des vignettes pour ne rien
      // changer (deux mises en page forcees par toucher).
      if (variant.media_id) {
        const galerie = this.section.querySelector('[data-gallery]');
        if (galerie && !galerie.querySelector(`[data-media-id="${variant.media_id}"][data-active]`)) {
          galerie.select(variant.media_id);
        }
      }

      const url = new URL(this.data.url, window.location.origin);
      url.searchParams.set('variant', String(variant.id));
      // lmdh 12/10/2026 (lot AS, B8) : l adresse apres l affichage (voir apresAffichage)
      apresAffichage(() => window.history.replaceState({}, '', url));

      this.refreshPrice(url).finally(() => this.syncSticky());
    }

    /**
     * Marque les valeurs qui ne menent a aucune variante disponible,
     * en tenant compte des autres options deja choisies.
     */
    refreshAvailability() {
      const values = this.selection;

      this.root.querySelectorAll('input[type="radio"]').forEach((input) => {
        const position = Number(input.dataset.optionPosition) - 1;
        const candidate = [...values];
        candidate[position] = input.value;

        // Tant que le client n'a pas rempli les autres options, on ne peut
        // rien affirmer : mieux vaut ne rien barrer que barrer a tort.
        if (candidate.some((value) => value === undefined)) {
          input.removeAttribute('data-unavailable');
          return;
        }

        const match = this.find(candidate);
        input.toggleAttribute('data-unavailable', !this.unknown(candidate) && (!match || !match.available));
      });

      this.root.querySelectorAll('select[data-option-position]').forEach((select) => {
        const position = Number(select.dataset.optionPosition) - 1;
        [...select.options].forEach((optionEl) => {
          if (optionEl.value === '') return;

          const candidate = [...values];
          candidate[position] = optionEl.value;

          if (candidate.some((value) => value === undefined)) {
            optionEl.disabled = optionEl.value === '';
            optionEl.removeAttribute('data-unavailable');
            return;
          }

          const match = this.find(candidate);
          const unavailable = !this.unknown(candidate) && (!match || !match.available);
          optionEl.toggleAttribute('data-unavailable', unavailable);
          optionEl.disabled = unavailable && optionEl.value !== select.value;
        });
      });
    }

    async refreshPrice(url) {
      const sectionId = this.section.id.replace('shopify-section-', '');
      const request = new URL(url);
      request.searchParams.set('section_id', sectionId);

      this.controller?.abort();
      this.controller = new AbortController();

      const priceBlock = this.section.querySelector('[data-price-block]');
      priceBlock?.classList.add('is-updating');

      try {
        const response = await fetch(request, { signal: this.controller.signal });
        if (!response.ok) throw new Error(String(response.status));

        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');

        ['[data-price-block]', '[data-inventory]', '[data-sku]'].forEach((selector) => {
          const next = doc.querySelector(selector);
          const current = this.section.querySelector(selector);
          if (next && current) current.innerHTML = next.innerHTML;
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          // Le prix affiche reste celui de la variante precedente : mieux
          // vaut une valeur legerement en retard qu'un emplacement vide.
        }
      } finally {
        priceBlock?.classList.remove('is-updating');
      }
    }
  }

  /* ----------------------------------------------------------------------
   * <product-form> : ajout au panier sans rechargement
   * -------------------------------------------------------------------- */

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.button = this.querySelector('[type="submit"]');
      this.error = this.querySelector('[data-form-error]');
      this.form.addEventListener('submit', (event) => this.submit(event));
    }

    setBusy(busy) {
      if (!this.button) return;
      this.button.classList.toggle('is-busy', busy);
      this.button.setAttribute('aria-busy', String(busy));

      if (busy) {
        this.wasDisabled = this.button.disabled;
        this.button.disabled = true;
        return;
      }

      // Apres l'ajout, on restaure l'etat calcule par le selecteur de
      // variantes (taille manquante, rupture) plutot que de tout reactiver.
      const shouldDisable =
        this.wasDisabled ||
        this.button.dataset.state === 'added' ||
        this.button.hasAttribute('data-force-disabled');
      this.button.disabled = Boolean(shouldDisable);
    }

    showError(message) {
      if (this.error) {
        this.error.textContent = message;
        this.error.hidden = false;
      } else {
        window.LMH?.toast(message, 'error');
      }
    }

    async submit(event) {
      event.preventDefault();

      if (this.error) this.error.hidden = true;

      /* lmdh 05/10/2026 (rapport 351, U23) : « Choisir une taille » remonte a l option manquante et la signale */
      if (this.button?.dataset.mode === 'choisir') {
        const manque = optionManquante();
        if (manque) {
          signalerOption(manque);
          return;
        }
      }

      const idInput = this.form.querySelector('[data-variant-id]');
      if (this.button?.disabled || !idInput?.value || idInput.disabled) {
        let message = this.button?.dataset.labelChoose || '';
        try {
          const payload = document.querySelector('[data-variant-data]');
          if (payload) {
            message = JSON.parse(payload.textContent).labels?.incomplete || message;
          }
        } catch (error) {
          /* ignore */
        }
        this.showError(message || this.dataset.errorMessage || '');
        return;
      }

      /* lmdh 08/10/2026 (critique des fiches chaises) : quantite bornee de 1 a 10 avant l envoi, comme le panier
         (lot Z). Une saisie de 12 partait au panier (Entree ne passe pas par « change ») : la quantite revient a 10,
         le message WhatsApp s affiche, rien ne part. */
      const champQte = this.form.querySelector('[name="quantity"]');
      if (champQte) {
        // lmdh 08/10/2026 (lots AB3, AB4) : corrigee a l instant par « change » (Entree ou clic juste apres la saisie)
        // -> rien ne part, la cliente voit la quantite corrigee ; le message des 10 a deja ete pose par « change »
        if (Number(champQte.dataset.borneA || 0) > Date.now() - 1000) {
          champQte.dataset.borneA = '';
          return;
        }
        const maxQte = Number(champQte.max) || 0;
        const brut = String(champQte.value);
        const v = Math.floor(Number(brut.replace(',', '.')));
        const juste = !(v >= 1) ? 1 : maxQte && v > maxQte ? maxQte : v;
        if (String(juste) !== brut) {
          champQte.value = String(juste);
          champQte.dispatchEvent(new Event('change', { bubbles: true }));
          champQte.dataset.borneA = '';
          montrerMax(champQte, Boolean(maxQte) && v > maxQte);
          return;
        }
      }

      this.setBusy(true);

      const body = new FormData(this.form);
      const root = window.Shopify?.routes?.root || '/';
      const sectionIds =
        typeof window.LMH?.getCartSectionIds === 'function' ? window.LMH.getCartSectionIds() : [];

      if (sectionIds.length) {
        body.append('sections', sectionIds.join(','));
        body.append('sections_url', `${root}cart`);
      }

      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body,
        });

        const payload = await response.json();

        if (!response.ok) {
          this.showError(payload.description || payload.message || '');
          return;
        }

        const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then(
          (res) => res.json()
        );

        window.LMH?.publishCart(cart);

        if (this.button) {
          this.button.dataset.state = 'added';
          this.button.disabled = true;
          const label = this.querySelector('[data-add-label]');
          if (label && this.button.dataset.labelAdded) {
            label.textContent = this.button.dataset.labelAdded;
          }
          window.clearTimeout(this.addedTimer);
          this.addedTimer = window.setTimeout(() => {
            this.button.removeAttribute('data-state');
            if (label) {
              label.textContent = this.button.dataset.labelAvailable || label.textContent;
            }
            this.button.disabled = this.wasDisabled === true;
            document
              .querySelector('[data-variant-picker]')
              ?.dispatchEvent(new Event('change', { bubbles: true }));
          }, 2200);
        }

        document.dispatchEvent(
          new CustomEvent('lmh:cart:added', {
            detail: {
              item: payload,
              sections: payload.sections || null,
              cartPublished: true,
              returnFocus: this.button,
            },
          })
        );
      } catch (error) {
        this.showError(this.dataset.errorMessage || '');
      } finally {
        this.setBusy(false);
      }
    }
  }

  /* lmdh 30/09/2026 (rapport 334, B6) : des 2 articles, le total sous le bouton (« Total pour 6 : 101,40 € »), et ce
     qu il debloque au-dela du seuil ; le prix affiche restait celui d une seule housse, quelle que soit la quantite. */
  /* lmdh 05/10/2026 (rapport 352, U24) : paliers « 4:5|6:10 » de data-offre-paliers (champ produit custom.offre_paliers). */
  const lirePaliers = () => {
    const el = document.querySelector('[data-offre-paliers]');
    if (!el) return [];
    return el.dataset.offrePaliers
      .split('|')
      .map((p) => p.split(':').map((x) => Number(String(x).trim())))
      .filter(([q, pct]) => Number.isInteger(q) && q > 1 && pct > 0 && pct < 100)
      .sort((a, b) => a[0] - b[0]);
  };
  const majPuces = (q) => {
    document.querySelectorAll('[data-palier-qte]').forEach((b) => b.setAttribute('aria-pressed', String(Number(b.dataset.palierQte) === q)));
  };
  document.addEventListener('click', (e) => {
    const b = e.target.closest?.('[data-palier-qte]');
    const champ = document.querySelector('.buy [name="quantity"]');
    if (!b || !champ) return;
    champ.value = String(Number(b.dataset.palierQte));
    champ.dispatchEvent(new Event('change', { bubbles: true }));
  });

  const majTotal = () => {
    const prixBarre = document.querySelector('[data-sticky-price]');
    const texteBarre = textePrixBarre(document);
    if (prixBarre && texteBarre) poserPrixBarre(prixBarre, texteBarre);
    const ligne = document.querySelector('[data-buy-total]');
    const champ = document.querySelector('.buy [name="quantity"]');
    if (champ) majPuces(Math.max(1, Math.floor(Number(champ.value) || 1)));
    const prix = document.querySelector('[data-price-block] .price__amount');
    if (!ligne || !champ || !prix || !window.LMH?.formatMoney) return;
    const q = Math.max(1, Math.floor(Number(champ.value) || 1));
    const cents = Math.round(Number(prix.textContent.replace(/[^\d,]/g, '').replace(',', '.')) * 100);
    if (q < 2 || !cents) {
      ligne.hidden = true;
      ligne.textContent = '';
      return;
    }
    const total = cents * q;
    /* lmdh 05/10/2026 (rapport 352, U24) : au palier, le total remise, calcule comme le panier Shopify : la remise
       est arrondie a l'unite INFERIEURE, housse par housse (mesure sur un vrai panier le 05/10 : 4 x 16,90 = 3,36 EUR
       de remise et non 3,38 ; 2 x 16,90 + 2 x 19,90 = 3,66 et non 3,68). */
    let palier = null;
    for (const p of lirePaliers()) if (q >= p[0]) palier = p;
    const remise = palier ? Math.floor((cents * palier[1]) / 100) * q : 0;
    const net = total - remise;
    const seuil = Number(document.querySelector('[data-offer-gauge]')?.dataset.threshold || 0);
    // lmdh 09/10/2026 (critique des coussins) : sur un lot, la quantite compte des lots : « 2 lots, soit 4 housses »
    const parArticle = Number(ligne.dataset.houssesParArticle || 1);
    ligne.textContent =
      'Total para ' + q + (parArticle > 1 ? ' packs (' + q * parArticle + ' fundas)' : '') + ': ' + window.LMH.formatMoney(net) +
      (palier ? ' (−' + palier[1] + ' %, en lugar de ' + window.LMH.formatMoney(total) + ')' : '') +
      (seuil && net >= seuil ? ' · envío gratis y 2 fundas de cojín de regalo' : '');
    ligne.hidden = false;
  };
  document.addEventListener('input', (e) => { if (e.target.matches?.('.buy [name="quantity"]')) majTotal(); });
  document.addEventListener('change', (e) => { if (e.target.matches?.('.buy [name="quantity"]')) majTotal(); });
  document.addEventListener('click', (e) => { if (e.target.closest?.('.buy .quantity__step')) window.setTimeout(majTotal, 0); });

  /* lmdh 08/10/2026 (critique des fiches chaises, choix du proprietaire) : 10 au plus par article, comme le panier
     (lot Z). « + » s arretait a 10 sans rien dire et une saisie de 12 passait ; au-dela, le message WhatsApp de la
     fiche (buy-buttons, data-buy-max). Ecoute en capture : la quantite est bornee avant le total (majTotal). */
  const montrerMax = (champ, oui) => {
    const p = champ?.closest('.buy')?.querySelector('[data-buy-max]');
    if (p) p.hidden = !oui;
  };
  document.addEventListener('change', (e) => {
    const champ = e.target;
    if (!champ.matches?.('.buy [name="quantity"]')) return;
    const maxQte = Number(champ.max) || 0;
    const brut = String(champ.value);
    const v = Math.floor(Number(brut.replace(',', '.')));
    // lmdh 08/10/2026 (lot AB4) : vide, 0 ou moins -> 1 ; decimale -> partie entiere ; plus de 10 -> 10 et WhatsApp
    const juste = !(v >= 1) ? 1 : maxQte && v > maxQte ? maxQte : v;
    if (String(juste) !== brut) {
      champ.value = String(juste);
      champ.dataset.borneA = String(Date.now());
    }
    montrerMax(champ, Boolean(maxQte) && v > maxQte);
  }, true);
  document.addEventListener('click', (e) => {
    const pas = e.target.closest?.('.buy .quantity__step');
    const champ = pas?.closest('.buy')?.querySelector('[name="quantity"]');
    if (!champ) return;
    const maxQte = Number(champ.max) || 0;
    montrerMax(champ, Boolean(maxQte) && Number(pas.dataset.step) > 0 && Number(champ.value) >= maxQte);
  }, true);

  /* lmdh 08/10/2026 (critique des fiches chaises, choix du proprietaire) : a une housse des 85 EUR d articles, panier
     compris, la fiche propose la housse de plus (5 x 16,90 = 84,50 EUR : « Plus que 0,50 € … », « Passer à 6
     housses »). Aide a 15 EUR au plus, comme le panier (lot Z) ; jamais au-dela de 10. Fiches de chaise seulement
     (data-buy-pouce, buy-buttons). Total calcule comme majTotal (paliers de remise eventuels). */
  const netPour = (q, cents) => {
    let palier = null;
    for (const p of lirePaliers()) if (q >= p[0]) palier = p;
    return cents * q - (palier ? Math.floor((cents * palier[1]) / 100) * q : 0);
  };
  const eligiblePanier = (cart) =>
    (Array.isArray(cart?.items) ? cart.items : []).reduce(
      (s, it) => (it?.properties?._gift || it?.gift_card ? s : s + Number(it?.final_line_price || 0)),
      0
    );
  const majPouce = () => {
    const zone = document.querySelector('[data-buy-pouce]');
    const champ = document.querySelector('.buy [name="quantity"]');
    const prix = document.querySelector('[data-price-block] .price__amount');
    if (!zone || !champ || !prix || !window.LMH?.formatMoney) return;
    const seuil = Number(zone.dataset.seuil || 0);
    const panier = Number(zone.dataset.panier || 0);
    const maxQte = Number(champ.max) || 10;
    const q = Math.max(1, Math.floor(Number(champ.value) || 1));
    const cents = Math.round(Number(prix.textContent.replace(/[^\d,]/g, '').replace(',', '.')) * 100);
    const avant = panier + netPour(q, cents);
    const apres = panier + netPour(q + 1, cents);
    if (!seuil || !cents || q >= maxQte || avant >= seuil || apres < seuil || seuil - avant > 1500) {
      zone.hidden = true;
      return;
    }
    const court = window.LMH.formatMoneyCourt || window.LMH.formatMoney;
    zone.querySelector('[data-pouce-texte]').textContent =
      'Solo te faltan ' + court(seuil - avant) + ' para el envío gratis y las 2 fundas de cojín de regalo.';
    const bouton = zone.querySelector('[data-qte-cible]');
    bouton.dataset.qteCible = String(q + 1);
    bouton.textContent = 'Pasar a ' + (q + 1) + ' fundas';
    zone.hidden = false;
  };
  document.addEventListener('click', (e) => {
    const b = e.target.closest?.('[data-qte-cible]');
    const champ = document.querySelector('.buy [name="quantity"]');
    if (!b || !champ || !b.dataset.qteCible) return;
    champ.value = b.dataset.qteCible;
    champ.dispatchEvent(new Event('change', { bubbles: true }));
    // le bouton disparait avec le coup de pouce : le focus va au bouton d achat, pas sur la page
    const ajout = document.querySelector('[data-add-button]');
    if (ajout && !ajout.disabled) ajout.focus({ preventScroll: true });
  });
  document.addEventListener('input', (e) => { if (e.target.matches?.('.buy [name="quantity"]')) majPouce(); });
  document.addEventListener('change', (e) => { if (e.target.matches?.('.buy [name="quantity"]')) majPouce(); });
  document.addEventListener('lmh:cart:updated', (e) => {
    const zone = document.querySelector('[data-buy-pouce]');
    if (!zone || !e.detail?.cart) return;
    zone.dataset.panier = String(eligiblePanier(e.detail.cart));
    majPouce();
  });
  majPouce();

  /* ----------------------------------------------------------------------
   * Barre d'achat sticky (mobile)
   * -------------------------------------------------------------------- */

  const setupStickyBuy = () => {
    const sticky = document.querySelector('[data-sticky-buy]');
    const mainButton = document.querySelector('[data-add-button]');
    if (!sticky || !mainButton) return;
    if (sticky.dataset.bound === 'true') return;
    sticky.dataset.bound = 'true';

    const stickyAdd = sticky.querySelector('[data-sticky-add]');
    stickyAdd?.addEventListener('click', () => {
      if (!mainButton.disabled) {
        mainButton.click();
        return;
      }
      const manque = optionManquante();
      if (manque) signalerOption(manque);
    });

    sticky.hidden = false;
    // lmdh 05/10/2026 (audit V3.1, C2) : cachee sous l ecran, la barre sort de la tabulation et du lecteur d ecran
    sticky.inert = true;

    const stickyLabel = sticky.querySelector('[data-sticky-label]');
    const stickyButton = sticky.querySelector('[data-sticky-add]');
    const mainLabel = document.querySelector('[data-add-label]');
    const priceAmount = document.querySelector('[data-price-block] .price__amount');
    const stickyPrice = sticky.querySelector('[data-sticky-price]');

    if (stickyButton) etatSticky(sticky, mainButton, mainLabel?.textContent || stickyLabel?.textContent || '');
    // lmdh 03/10/2026 (rapport 346, U4) : meme regle que syncSticky, le « Dès » vit dans un autre span
    if (stickyPrice && priceAmount) {
      // lmdh 12/10/2026 (lot AQ, N2) : la meme fonction que les deux autres mises a jour du prix de la barre
      poserPrixBarre(stickyPrice, textePrixBarre(document));
    }

    /* lmdh 05/10/2026 (rapport 351, U28) : la barre se retire aussi quand le pied de page arrive a l ecran ; son
       bouton corail et « Je m'inscris » de la lettre se voyaient ensemble sur telephone. */
    let piedVisible = false;
    /* lmdh 07/10/2026 (critique EasyDrap, U46) : a l arrivee, la barre couvrait la phrase d accroche (92 a 100 %, sur
       les 5 fiches EasyDrap) ; elle attend que la page ait defile de sa propre hauteur. */
    const seuilDefile = () => (sticky.offsetHeight || 80) + 24;
    /* lmdh 12/10/2026 (lot AO, U31) : la barre apparaissait 48 a 64 px trop tard : l en-tete collant couvre le bouton
       avant qu il ne sorte de l ecran, et l observateur ne comptait que l ecran. Le bouton compte pour la part visible SOUS
       l en-tete (15 % au moins, bas de l ecran a -8 % comme avant). Et la barre reste cachee tant que les tuiles de taille
       (sans taille : les choix de la fiche) sont a l ecran : elle les couvrait, a 390 comme a 1 366 x 768. */
    const enTete = document.querySelector('[data-header]');
    const zonesChoix = () => {
      const picker = document.querySelector('[data-variant-picker]');
      if (!picker) return [];
      const tailles = [...picker.querySelectorAll('.variant__options--tiles, .variant__options--select')];
      return tailles.length ? tailles : [...picker.querySelectorAll('[data-variant-option]')];
    };
    let prevu = false;
    const appliquer = () => {
      prevu = false;
      const haut = enTete ? Math.max(0, enTete.getBoundingClientRect().bottom) : 0;
      const r = mainButton.getBoundingClientRect();
      const boutonVisible = r.height > 0 && Math.min(r.bottom, window.innerHeight * 0.92) - Math.max(r.top, haut) >= r.height * 0.15;
      const choixVisibles = zonesChoix().some((z) => {
        const q = z.getBoundingClientRect();
        return q.height > 0 && q.bottom > haut && q.top < window.innerHeight;
      });
      const visible = !boutonVisible && !choixVisibles && !piedVisible && window.scrollY > seuilDefile();
      sticky.toggleAttribute('data-visible', visible);
      // lmdh 12/10/2026 (lot AS, B1) : inert n est reecrit que s il change (le reposer a chaque image du defilement
      // reecrivait l attribut et invalidait le style de la barre). Les deux bascules forcees restent : elles n ecrivent
      // rien quand l etat est le meme, et cart.js peut retirer has-sticky-buy (la barre le repose a l image suivante).
      if (sticky.inert !== !visible) sticky.inert = !visible;
      document.documentElement.classList.toggle('has-sticky-buy', visible);
    };
    const planifier = () => {
      if (prevu) return;
      prevu = true;
      requestAnimationFrame(appliquer);
    };
    const observer = new IntersectionObserver(planifier, { threshold: [0, 0.15, 0.5, 1] });
    observer.observe(mainButton);
    zonesChoix().forEach((z) => observer.observe(z));
    window.addEventListener('scroll', planifier, { passive: true });
    window.addEventListener('resize', planifier, { passive: true });
    const pied = document.querySelector('footer.footer');
    if (pied) {
      new IntersectionObserver(([entry]) => {
        piedVisible = entry.isIntersecting;
        planifier();
      }).observe(pied);
    }
    planifier();
  };

  /* ----------------------------------------------------------------------
   * Demarrage
   * -------------------------------------------------------------------- */

  if (!customElements.get('media-gallery')) customElements.define('media-gallery', MediaGallery);
  if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);

  /* lmdh 06/10/2026 (critique, P2) : « Voir la suite » devient « Voir moins » une fois la liste ouverte (9 rangees de
     pastilles sans retour possible). En se repliant, la liste revient a l ecran si son haut en etait sorti. */
  const etatRepli = (btn, ouvert) => {
    if (btn.dataset.libelle === undefined) {
      btn.dataset.libelle = btn.innerHTML;
      btn.dataset.aria = btn.getAttribute('aria-label') || '';
    }
    btn.innerHTML = ouvert ? 'Ver menos' : btn.dataset.libelle;
    if (ouvert) btn.setAttribute('aria-label', 'Ver menos opciones');
    else if (btn.dataset.aria) btn.setAttribute('aria-label', btn.dataset.aria);
    btn.setAttribute('aria-expanded', String(ouvert));
  };

  /* lmdh 06/10/2026 (seconde critique, P2) : bouton de suite d une vue filtree (setupFamillesFiche) */
  const etatFiltre = (btn, fold, ouvert) => {
    const reste = fold.querySelectorAll('label.variant__label.is-filtre-replie').length;
    btn.hidden = reste === 0;
    btn.innerHTML = ouvert ? 'Ver menos' : 'Ver más <span aria-hidden="true">(+' + reste + ')</span>';
    btn.setAttribute('aria-label', ouvert ? 'Ver menos opciones' : 'Ver ' + reste + ' colores más de este tono');
    btn.setAttribute('aria-expanded', String(ouvert));
  };

  const setupSwatchFold = (picker) => {
    picker.querySelectorAll('[data-swatch-more]').forEach((btn) => {
      if (btn.dataset.ready) return;
      btn.dataset.ready = 'true';
      const fold = btn.closest('.variant')?.querySelector('[data-swatch-fold]');
      if (!fold) return;
      etatRepli(btn, !fold.classList.contains('is-collapsed'));
      btn.addEventListener('click', () => {
        let ouvrir;
        if (fold.dataset.filtre && fold.dataset.filtre !== 'tous') {
          ouvrir = fold.classList.toggle('is-filtre-ouvert');
          etatFiltre(btn, fold, ouvrir);
        } else {
          ouvrir = fold.classList.contains('is-collapsed');
          fold.classList.toggle('is-collapsed', !ouvrir);
          etatRepli(btn, ouvrir);
        }
        if (ouvrir) return;
        const bloc = btn.closest('.variant');
        const haut = bloc.getBoundingClientRect().top;
        const entete = document.querySelector('.header, header')?.getBoundingClientRect().height || 0;
        if (haut < entete) {
          window.scrollTo({ top: Math.max(0, haut + window.scrollY - entete - 8), behavior: reduce.matches ? 'auto' : 'smooth' });
        }
      });
    });
  };

  /* lmdh 30/09/2026 (rapport 334, B10) : au-dela de 20 coloris, des puces « Tous · Unis · Motifs · Gris · … »
     au-dessus des pastilles : « je veux un bleu » en un geste, au lieu de 71 essais. Famille lue dans la table unique
     (LMH.familleColoris) ; uni ou motif lu sur la pastille (couleur ou image de la bibliotheque). */
  const LIMITE_FILTRE = 12;

  const setupFamillesFiche = (picker) => {
    const L = window.LMH;
    if (!L?.familleColoris) return;
    picker.querySelectorAll('.variant__options').forEach((boite) => {
      if (boite.dataset.familles) return;
      const labels = [...boite.querySelectorAll('label.variant__label[title]')];
      // lmdh 09/10/2026 (lot AH, critique des canapes) : de 13 a 20 coloris, « Tous · Unis · Motifs » seulement, et
      // seulement si la liste mele unis et motifs (housse de canape deperlante : 10 unis, 8 motifs)
      if (labels.length <= LIMITE_FILTRE) return;
      const court = labels.length <= 20;
      const nommeBase = boite.classList.contains('is-nomme');
      boite.dataset.familles = 'true';
      const nom = (l) => l.getAttribute('title') || '';
      // lmdh 30/09/2026 (retours du proprietaire) : la bibliotheque coloris peint les pastilles par feuille de style,
      // la classe --image n etait jamais posee et les 71 coloris de l extensible tombaient en Unis. Motif = pastille
      // peinte par une image (le dessin du tissu) ; un chine reste un uni : c est un fil melange, pas un dessin.
      const estMotif = (l) => {
        const s = l.querySelector('.variant__swatch');
        if (!s || /chiné|jaspeado/i.test(nom(l))) return false;
        return s.classList.contains('variant__swatch--image') || /url\(/.test(getComputedStyle(s).backgroundImage);
      };
      const photos = labels.some((l) => l.querySelector('.variant__swatch--photo'));
      const filtres = [{ cle: 'tous', nom: 'Todos', test: () => true }];
      if (!photos && labels.some(estMotif) && !labels.every(estMotif)) {
        filtres.push({ cle: 'unis', nom: 'Lisos', test: (l) => !estMotif(l) });
        filtres.push({ cle: 'motifs', nom: 'Estampados', test: estMotif });
      }
      // lmdh 09/10/2026 (lot AH2) : au moins 2 unis et 2 motifs, sinon un filtre a un seul coloris
      if (court && (filtres.length < 3 || labels.filter(estMotif).length < 2 || labels.filter((l) => !estMotif(l)).length < 2)) return;
      if (!court) L.FAMILLES.forEach((f) => {
        const test = (l) => L.familleColoris(nom(l)) === f;
        if (labels.some(test)) filtres.push({ cle: f.cle, nom: f.nom, test, couleur: f.couleur });
      });
      const rangee = document.createElement('div');
      rangee.className = 'variant__familles';
      rangee.setAttribute('role', 'group');
      rangee.setAttribute('aria-label', 'Filtrar los colores');
      let bascule = null;
      filtres.forEach((f, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'variant__famille' + (f.couleur ? ' variant__famille--teinte' : '');
        b.setAttribute('aria-pressed', String(i === 0));
        b.innerHTML = (f.couleur ? '<span class="variant__famille-dot" style="background:' + f.couleur + '"></span>' : '') + f.nom;
        b.addEventListener('click', () => {
          rangee.querySelectorAll('.variant__famille[aria-pressed]').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
          const plus = boite.closest('.variant')?.querySelector('[data-swatch-more]');
          /* lmdh 06/10/2026 (seconde critique, P2) : une vue filtree montre 12 coloris nommes puis « Voir la suite »
             (le filtre Motifs depliait 49 vignettes d un bloc, 1 743 px sur telephone). Sans bouton de suite (liste
             courte, pas de repli), rien n est replie. */
          // pas de repli pour cacher 1 a 3 coloris : « Voir la suite (+1) » coute plus qu il ne range
          const nbFiltre = f.cle === 'tous' ? 0 : labels.filter(f.test).length;
          const replier = Boolean(plus) && f.cle !== 'tous' && nbFiltre > LIMITE_FILTRE + 3;
          let rang = 0;
          labels.forEach((l) => l.classList.toggle('is-filtre-replie', replier && f.test(l) && rang++ >= LIMITE_FILTRE));
          boite.classList.remove('is-filtre-ouvert');
          boite.dataset.filtre = f.cle;
          if (f.cle !== 'tous') {
            boite.classList.remove('is-collapsed');
            if (plus) etatFiltre(plus, boite, false);
          } else if (plus && rangee.dataset.actif && rangee.dataset.actif !== 'tous') {
            // lmdh 06/10/2026 (critique, P2) : retour a « Tous » : la liste se replie comme au chargement
            boite.classList.add('is-collapsed');
            plus.hidden = false;
            etatRepli(plus, false);
          }
          rangee.dataset.actif = f.cle;
          // lmdh 06/10/2026 (critique, P2) : filtre actif = coloris nommes (variant-picker.liquid, .is-nomme)
          boite.classList.toggle('is-nomme', nommeBase || f.cle !== 'tous');
          if (bascule) bascule.classList.toggle('is-actif', Boolean(f.couleur));
          labels.forEach((l) => l.classList.toggle('is-hors-famille', !f.test(l)));
        });
        rangee.appendChild(b);
      });
      /* lmdh 06/10/2026 (critique, P2) : sur ordinateur, les 13 teintes passaient sur 4 lignes et repoussaient le bouton
         d achat sous la ligne de flottaison. Elles se rangent derriere « Teintes », toutes visibles en un geste
         (critical.css) ; sur telephone la bascule est cachee et la rangee defile sur une ligne, comme avant. */
      const premiereTeinte = rangee.querySelector('.variant__famille--teinte');
      if (premiereTeinte) {
        bascule = document.createElement('button');
        bascule.type = 'button';
        bascule.className = 'variant__famille variant__famille--teintes';
        bascule.setAttribute('aria-expanded', 'false');
        bascule.textContent = 'Tonos';
        bascule.addEventListener('click', () => {
          const ouvert = rangee.classList.toggle('is-teintes');
          bascule.setAttribute('aria-expanded', String(ouvert));
        });
        rangee.insertBefore(bascule, premiereTeinte);
      }
      boite.before(rangee);
      const fin = () => rangee.classList.toggle('is-fin', rangee.scrollLeft + rangee.clientWidth >= rangee.scrollWidth - 4);
      rangee.addEventListener('scroll', fin, { passive: true });
      window.addEventListener('resize', fin);
      fin();
    });
  };

  /* lmdh 12/10/2026 (lot AO, U32) : au-dela de 20 coloris, la vue « Tous » montre des pastilles sans nom (57 motifs sur
     l extensible ; jete tisse 48, lot de coussins 31, fauteuil extensible 23). Sur telephone, quand la legende « Couleur :
     … » et sa vignette passent sous l en-tete alors que les pastilles sont encore a l ecran, un rappel du coloris choisi
     (vignette et nom) se cale sous l en-tete et suit chaque toucher. Il n ajoute rien a la hauteur de la fiche : le bouton
     d achat ne descend pas (U58), ce que des noms sous 71 pastilles auraient fait (environ 350 px). La legende reste
     l information des lecteurs d ecran : le rappel leur est cache. Regle de nombre, jamais de gamme. */
  const setupRappelColoris = (picker) => {
    if (picker.dataset.rappel) return;
    const champ = [...picker.querySelectorAll('[data-variant-option]')].find(
      (f) => f.querySelectorAll('.variant__options--swatches label.variant__label[title]').length > 20
    );
    if (!champ) return;
    picker.dataset.rappel = 'true';
    const legende = champ.querySelector('legend');
    const grille = champ.querySelector('.variant__options--swatches');
    const valeur = champ.querySelector('[data-option-selected]');
    const vignette = champ.querySelector('[data-color-preview] img');
    const nomOption = (champ.querySelector('.variant__name')?.textContent || 'Color').trim();
    const rappel = document.createElement('div');
    rappel.className = 'lmdh-rappel-coloris';
    rappel.setAttribute('aria-hidden', 'true');
    const img = document.createElement('img');
    img.alt = '';
    img.width = 40;
    img.height = 40;
    img.decoding = 'async';
    const texte = document.createElement('span');
    texte.className = 'lmdh-rappel-coloris__texte';
    const nom = document.createElement('strong');
    texte.append(nomOption + ': ', nom);
    rappel.append(img, texte);
    document.body.appendChild(rappel);
    const tel = window.matchMedia('(max-width: 989px)');
    const enTete = document.querySelector('[data-header]');
    const recopier = () => {
      nom.textContent = (valeur?.textContent || '').trim();
      // lmdh 12/10/2026 (lot AO2) : l attribut d abord ; currentSrc garde l ancienne photo tant que la nouvelle charge
      const src = vignette ? vignette.getAttribute('src') || vignette.currentSrc || '' : '';
      if (src && img.getAttribute('src') !== src) img.src = src;
      img.hidden = !src;
    };
    let prevu = false;
    const placer = () => {
      prevu = false;
      const haut = enTete ? Math.max(0, enTete.getBoundingClientRect().bottom) : 0;
      const l = legende.getBoundingClientRect();
      const g = grille.getBoundingClientRect();
      const montrer = tel.matches && Boolean(nom.textContent) && l.bottom < haut && g.bottom > haut + 72 && g.top < window.innerHeight;
      // lmdh 12/10/2026 (lot AS, B1) : la position n est reecrite que si elle change
      if (rappel.style.top !== haut + 'px') rappel.style.top = haut + 'px';
      rappel.classList.toggle('is-visible', montrer);
    };
    const planifier = () => {
      if (prevu) return;
      prevu = true;
      requestAnimationFrame(placer);
    };
    recopier();
    if (valeur) new MutationObserver(() => { recopier(); planifier(); }).observe(valeur, { childList: true, characterData: true, subtree: true });
    if (vignette) new MutationObserver(recopier).observe(vignette, { attributes: true, attributeFilter: ['src'] });
    rappel.addEventListener('click', () => {
      const h = enTete ? enTete.getBoundingClientRect().height : 0;
      window.scrollTo({ top: Math.max(0, champ.getBoundingClientRect().top + window.scrollY - h - 8), behavior: reduce.matches ? 'auto' : 'smooth' });
    });
    window.addEventListener('scroll', planifier, { passive: true });
    window.addEventListener('resize', planifier, { passive: true });
    planifier();
  };

  const init = (root = document) => {
    root.querySelectorAll('[data-variant-picker]').forEach((picker) => {
      setupSwatchFold(picker);
      setupFamillesFiche(picker);
      setupRappelColoris(picker);
      if (picker.dataset.ready) return;
      picker.dataset.ready = 'true';
      new VariantPicker(picker);
    });
  };

  /* lmdh 06/10/2026 (critique, P2) : sur ordinateur, la galerie accompagne la colonne d achat (environ 60 % de la
     largeur restait vide en defilant). Plus haute que l ecran, elle se cale par le BAS (haut negatif) : ses vignettes
     restent visibles. La regle position: sticky est dans sections/product.liquid. */
  const setupGalerieCollante = () => {
    const media = document.querySelector('.product-layout__media');
    if (!media || media.dataset.collante) return;
    media.dataset.collante = 'true';
    const ordi = window.matchMedia('(min-width: 990px)');
    const caler = () => {
      if (!ordi.matches) {
        media.style.removeProperty('--lmdh-galerie-haut');
        return;
      }
      const entete = document.querySelector('.header, header')?.getBoundingClientRect().height || 72;
      const libre = window.innerHeight - media.offsetHeight - 16;
      media.style.setProperty('--lmdh-galerie-haut', Math.round(Math.min(entete + 16, libre)) + 'px');
    };
    if ('ResizeObserver' in window) new ResizeObserver(caler).observe(media);
    window.addEventListener('resize', caler, { passive: true });
    caler();
  };

  init();
  setupStickyBuy();
  setupGalerieCollante();
  document.addEventListener('shopify:section:load', (event) => {
    init(event.target);
    setupStickyBuy();
    setupGalerieCollante();
  });
})();
